import { api } from '../api.js';
import { el, clear } from '../dom.js';
import { t } from '../i18n.js';

// The facet editor. Structure, beside the free tag input rather than instead
// of it — the two layers do different jobs and the form has to show that.
//
// **Formats and styles appear once a domain is chosen.** They are dependent
// facets: `techno` is meaningless outside Music and `vegan` outside Food, so
// listing all of them at once would present two hundred options for a decision
// that has three. Setting is the one orthogonal facet and is always visible.
//
// The vocabulary is fetched, never hard-coded, because moderators extend it —
// a fourth domain has to be an insert, not a deploy, and a list in this file
// would quietly make that untrue.

export function facetField({ initial = [] } = {}) {
  const chosen = new Set(initial);

  const domainRow = el('div', { class: 'facet-row' });
  const scoped = el('div', { class: 'facet-scoped' });
  const settingRow = el('div', { class: 'facet-row' });

  // Indoor or outdoor is the one facet that is required, and it starts on
  // indoor. Most events are indoor, and the wrong default is recoverable in a
  // way its absence is not: an indoor event shown to somebody looking for an
  // open air is a disappointment, an open air missing from the open-air filter
  // is invisible. A required field with no default would be a form that cannot
  // be submitted until somebody guesses which of two words we want.
  if (![...chosen].some((entry) => entry.startsWith('setting:'))) chosen.add('setting:indoor');

  const node = el('fieldset', { class: 'field facet-field' }, [
    el('legend', {}, t.fieldFacets),
    el('div', { class: 'facet-group' }, [el('span', { class: 'facet-label' }, t.fieldDomain), domainRow]),
    scoped,
    el('div', { class: 'facet-group' }, [el('span', { class: 'facet-label' }, t.fieldSetting), settingRow]),
    el('p', { class: 'card-meta' }, t.facetHint),
  ]);

  let tree = { domains: [], settings: [] };

  api.get('/taxonomy').then((loaded) => {
    tree = loaded;
    paint();
  }).catch(() => {
    // A failed vocabulary must not block saving: tags still classify, and the
    // publish rule takes either. Silent rather than an error banner, because
    // nothing the publisher can do here would fix it.
    node.hidden = true;
  });

  function paint() {
    clear(domainRow);
    for (const domain of tree.domains) {
      domainRow.append(toggle(`domain:${domain.slug}`, domain.name));
    }

    clear(settingRow);
    // A switch rather than two chips: it is one question with two answers, and
    // exactly one of them is always true.
    const switchNode = el('div', { class: 'switch', role: 'group', 'aria-label': t.fieldSetting });
    for (const setting of tree.settings) {
      switchNode.append(settingHalf(setting));
    }
    settingRow.append(switchNode);

    paintScoped();
  }

  // The union over the chosen domains, not one domain at a time: a supper club
  // with a DJ is both, and that is the interesting case rather than the edge.
  function paintScoped() {
    clear(scoped);
    for (const domain of tree.domains) {
      if (!chosen.has(`domain:${domain.slug}`)) continue;

      // Only the axes this domain actually has. "Sonstiges" carries formats
      // and no styles — a flea market and a panel share no axis a style could
      // name — and a labelled empty row would read as a control that failed to
      // load rather than as a question that does not apply.
      if (domain.formats.length > 0) {
        scoped.append(el('div', { class: 'facet-group' }, [
          el('span', { class: 'facet-label' }, `${t.fieldFormat} · ${domain.name}`),
          el('div', { class: 'facet-row' }, domain.formats.map((f) => toggle(`format:${f.slug}`, f.name))),
        ]));
      }
      if (domain.styles.length > 0) {
        scoped.append(el('div', { class: 'facet-group' }, [
          el('span', { class: 'facet-label' }, `${domain.styleLabel} · ${domain.name}`),
          el('div', { class: 'facet-row' }, domain.styles.map((f) => toggle(`style:${f.slug}`, f.name))),
        ]));
      }
    }
  }

  // Radios, not checkboxes: the setting is required and single-valued, so the
  // control cannot offer a state the form would reject. Picking one clears the
  // other for free, which is the whole reason radios exist.
  function settingHalf(setting) {
    const value = `setting:${setting.slug}`;
    const input = el('input', { type: 'radio', name: 'ff-setting', value });
    input.checked = chosen.has(value);

    const half = el('label', { class: 'switch-half' }, [input, el('span', {}, setting.name)]);
    if (input.checked) half.setAttribute('data-active', '');

    input.addEventListener('change', () => {
      for (const other of tree.settings) chosen.delete(`setting:${other.slug}`);
      chosen.add(value);
      paint();
    });

    return half;
  }

  function toggle(value, label) {
    const button = el('button', {
      class: 'facet-chip', type: 'button', 'aria-pressed': chosen.has(value) ? 'true' : 'false',
    }, label);

    button.addEventListener('click', () => {
      if (chosen.has(value)) {
        chosen.delete(value);
        // Dropping a domain drops what hung off it. Leaving `style:techno`
        // behind after Music is switched off would send a facet the editor no
        // longer shows — invisible state is how a form starts lying.
        if (value.startsWith('domain:')) {
          const slug = value.slice('domain:'.length);
          const domain = tree.domains.find((entry) => entry.slug === slug);
          for (const facet of [...(domain?.formats ?? []), ...(domain?.styles ?? [])]) {
            chosen.delete(`format:${facet.slug}`);
            chosen.delete(`style:${facet.slug}`);
          }
        }
      } else {
        chosen.add(value);
        // A format or style implies its domain. The server writes it anyway;
        // doing it here too keeps the control from showing a chosen format
        // under a domain that looks unchosen.
        const owner = tree.domains.find((domain) =>
          domain.formats.some((f) => `format:${f.slug}` === value)
          || domain.styles.some((f) => `style:${f.slug}` === value));
        if (owner) chosen.add(`domain:${owner.slug}`);
      }
      paint();
    });

    return button;
  }

  return { node, value: () => [...chosen] };
}
